import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Native-app back behaviour for the dashboard's OWN scroll container.
 *
 * React Router's <ScrollRestoration/> tracks `window`, but every page here
 * scrolls inside DashboardLayout's `overflow-auto` div — so Back was always
 * landing at the top (THI-53 follow-up, raised from the installed PWA where
 * it reads as "web pretending to be an app").
 *
 * Positions are keyed by `location.key` (unique per history ENTRY, so two
 * visits to the same student each restore their own position) and kept in
 * sessionStorage: survives a reload, dies with the tab — exactly the
 * lifetime of the history stack itself.
 *
 * POP (back/forward gesture) restores; PUSH/REPLACE scrolls to top, which is
 * the other half of what a native app does. Restore runs in a layout effect
 * so the paint the user sees is already at the right offset; if the list is
 * shorter than it was (data changed), the browser clamps silently.
 */
const KEY = 'lophoc.scroll';

function load(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

export function useScrollRestoration(ref: RefObject<HTMLElement | null>) {
  const location = useLocation();
  const navigationType = useNavigationType();
  // One in-memory copy per mount; sessionStorage is the backup, not the hot path.
  const positions = useRef<Record<string, number> | null>(null);
  if (positions.current === null) positions.current = load();

  // Save continuously (scroll events don't fire on unmount, and the unmount
  // order versus the new page's effect is not worth betting on).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      positions.current![location.key] = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      try {
        sessionStorage.setItem(KEY, JSON.stringify(positions.current));
      } catch {
        /* quota/private mode — restoration degrades to top, nothing breaks */
      }
    };
  }, [ref, location.key]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = navigationType === 'POP' ? (positions.current![location.key] ?? 0) : 0;
    // location.key identifies the history entry; navigationType rides along.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, location.key]);
}
